/**
 * TextareaQuestion Component
 * Handles multi-line text input
 */

interface TextareaQuestionProps {
  questionId: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  error?: string
  rows?: number
}

const TextareaQuestion = ({
  questionId,
  value,
  onChange,
  placeholder = 'Type your answer here...',
  required = false,
  error,
  rows = 4
}: TextareaQuestionProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className="w-full max-w-2xl">
      <textarea
        id={questionId}
        name={questionId}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        rows={rows}
        autoFocus
        className={`
          w-full px-6 py-4 text-lg border-2 rounded-lg transition-all duration-200 resize-vertical
          focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
          ${
            error
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 bg-white focus:border-primary-green'
          }
        `}
        aria-invalid={!!error}
        aria-describedby={error ? `${questionId}-error` : undefined}
      />

      {/* Character Count */}
      {value.length > 0 && (
        <div className="mt-2 text-sm text-gray-500 text-right">
          {value.length} characters
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          id={`${questionId}-error`}
          className="mt-2 text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}
    </div>
  )
}

export default TextareaQuestion
